import { ApplicationInsights, DistributedTracingModes } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights | null = null;

export function initFrontendTelemetry(): void {
  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;

  if (!connectionString) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info('[Telemetry] VITE_APPINSIGHTS_CONNECTION_STRING not set – telemetry disabled');
    }
    return;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        disableFetchTracking: false,
        enableCorsCorrelation: true,
        // W3C-only: send traceparent, not the legacy Request-Id header.
        // Request-Id is not in the API's CORS allow-list; sending it fails
        // the preflight and breaks every instrumented cross-origin call.
        distributedTracingMode: DistributedTracingModes.W3C,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        disableExceptionTracking: false,
        maxBatchInterval: 5000,
        // Chrome blocks unload listeners under Permissions-Policy; AI's
        // EventHelpers otherwise logs "Permissions policy violation: unload".
        disablePageUnloadEvents: ['unload', 'beforeunload'],
      },
    });

    appInsights.loadAppInsights();
    appInsights.trackPageView();

    // eslint-disable-next-line no-console
    console.info('[Telemetry] Application Insights initialized');
  } catch (error) {
    console.error('[Telemetry] Failed to initialize Application Insights:', error);
    appInsights = null;
  }
}

export function trackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>,
): void {
  appInsights?.trackEvent({ name, properties, measurements });
}

export function trackException(error: Error, properties?: Record<string, string>): void {
  appInsights?.trackException({ exception: error, properties });
}

export function trackMetric(
  name: string,
  average: number,
  properties?: Record<string, string>,
): void {
  appInsights?.trackMetric({ name, average, properties });
}

export function setAuthenticatedUser(userId: string, accountId?: string): void {
  appInsights?.setAuthenticatedUserContext(userId, accountId, true);
}

export function clearAuthenticatedUser(): void {
  appInsights?.clearAuthenticatedUserContext();
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}
